import type { Track, PlaybackKind } from '@inscribed/player';
import { derivedTitle } from '@inscribed/player';
import type { AudioRecord, Proposal } from './api';

/**
 * Turning a catalog record into something the player can queue.
 *
 * This is where the IA6 distinctions earn their keep. A record is only `waveform` when the
 * browser can genuinely play and seek it; a MIDI or tracker module is `module` — identified,
 * inspectable, and **not** handed to an `<audio>` element that cannot decode it; a `no_media`
 * record is `none` and is never offered at all.
 *
 * Getting this wrong is how "12 valid MIDI files" turns back into "12 broken audio files" in the
 * UI, so the mapping is a pure function with its own tests rather than logic inside a component.
 */

/** Containers a browser `<audio>` element can decode, so they are seekable. */
const WAVEFORM_CONTAINERS = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/ogg',
  'audio/wav',
  'audio/flac',
  'audio/mp4',
  'audio/x-m4a',
  'audio/aac',
  'audio/opus',
  'video/mp4',
  'video/webm',
]);

/** Containers we identify but cannot play in the browser. Not failures — a different kind. */
const MODULE_CONTAINERS = new Set(['audio/midi', 'audio/xm', 'audio/mod']);

export function playbackKind(record: AudioRecord): PlaybackKind {
  // A declaration over noise is not media at all, whatever it claims.
  if (!record.playable || record.mismatch_class === 'no_media') return 'none';

  const container = (record.detected_container ?? record.declared_content_type ?? '').toLowerCase();
  if (MODULE_CONTAINERS.has(container)) return 'module';
  if (WAVEFORM_CONTAINERS.has(container)) return 'waveform';
  // An unrecognised container has bytes but no player we can promise; treat it as unplayable
  // rather than handing the element something it will reject.
  return 'none';
}

/**
 * The best human-facing name available, and where it came from.
 *
 * A curated title is presented as a title; a community proposal is attributed to the community;
 * otherwise a name derived from verified facts is used and **labelled as derived**. Nothing is
 * fabricated, and the UI can always tell the difference.
 */
export function titleFor(record: AudioRecord): { title: string; source: Track['titleSource'] } {
  const curated = record.community?.proposals?.title?.find((p: Proposal) => p.status === 'curated');
  if (curated) return { title: curated.value, source: 'curated' };

  const community = record.community?.proposals?.title
    ?.filter((p: Proposal) => p.status === 'community')
    // Most-confirmed first; the ranking already does this server-side, this is belt and braces.
    ?.sort((a, b) => b.confirmations - a.confirmations)[0];
  if (community) return { title: community.value, source: 'community' };

  return {
    title: derivedTitle({ inscriptionId: record.id, blockHeight: record.block_height }),
    source: 'derived',
  };
}

/** A record as a queueable track. */
export function toTrack(record: AudioRecord): Track {
  const { title, source } = titleFor(record);
  const kind = playbackKind(record);
  return {
    id: record.id,
    title,
    titleSource: source,
    container: record.detected_container ?? record.declared_content_type,
    // The catalog's probed duration, and for MIDI the duration the SMF tempo map computed — the
    // module is inspectable even though it is not playable.
    durationMs: record.probe?.duration_ms ?? record.midi?.duration_ms,
    blockHeight: record.block_height,
    playable: kind !== 'none',
    kind,
  };
}

/** Whether a record is worth offering as something to play. */
export function isPlayable(record: AudioRecord): boolean {
  return playbackKind(record) !== 'none';
}

/** A short, honest description of what a record is. Used on cards and the detail page. */
export function describe(record: AudioRecord): string {
  const container = record.detected_container ?? record.declared_content_type;
  const parts: string[] = [container];
  if (record.probe?.codec_name) parts.push(record.probe.codec_name);
  if (record.mismatch_class === 'no_media') parts.push('no media in body');
  else if (record.mismatch_class === 'declared_type_mismatch') parts.push('declared type does not match the bytes');
  return parts.join(' · ');
}
